import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../utils/supabaseClient";
import { ToastContainer, toast } from "react-toastify";
import {
  Image as ImageIcon,
  Edit2,
  Trash2,
  Link,
  Download,
} from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import "react-toastify/dist/ReactToastify.css";
import "../styles/ProductManagement.css";

const ProductManagement = () => {
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    image_url: "",
    product_link: "",
    download_link: "",
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const { data, error } = await supabase
        .from("partners")
        .select("*")
        .order("name");

      if (error) throw error;
      setPartners(data);
    } catch (error) {
      setError(error.message);
      toast.error("Failed to fetch partners");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = useCallback(async () => {
    if (!selectedPartner) return;

    try {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("partner_id", selectedPartner.id)
        .order("title");

      if (error) throw error;
      setProducts(data);
    } catch (error) {
      setError(error.message);
      toast.error("Failed to fetch products");
    }
  }, [selectedPartner]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  const uploadImage = async (file) => {
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()
        .toString(36)
        .substring(2)}${Date.now()}.${fileExt}`;
      const filePath = `products/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("images").getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      throw new Error("Error uploading image: " + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let imageUrl = formData.image_url;

      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      if (isEditing) {
        const { error } = await supabase
          .from("products")
          .update({
            title: formData.title,
            image_url: imageUrl,
            product_link: formData.product_link,
            download_link: formData.download_link,
            partner_id: selectedPartner.id,
          })
          .eq("id", currentProduct.id);

        if (error) throw error;
        toast.success("Product updated successfully!");
      } else {
        const { error } = await supabase.from("products").insert([
          {
            title: formData.title,
            image_url: imageUrl,
            product_link: formData.product_link,
            download_link: formData.download_link,
            partner_id: selectedPartner.id,
          },
        ]);

        if (error) throw error;
        toast.success("Product created successfully!");
      }

      setFormData({
        title: "",
        image_url: "",
        product_link: "",
        download_link: "",
      });
      setImageFile(null);
      setIsEditing(false);
      setCurrentProduct(null);
      fetchProducts();
    } catch (error) {
      setError(error.message);
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setCurrentProduct(product);
    setFormData({
      title: product.title,
      image_url: product.image_url,
      product_link: product.product_link,
      download_link: product.download_link,
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const { error } = await supabase.from("products").delete().eq("id", id);

        if (error) throw error;
        toast.success("Product deleted successfully!");
        fetchProducts();
      } catch (error) {
        setError(error.message);
        toast.error("An error occurred while deleting the product.");
      }
    }
  };

  const handleCancel = () => {
    setFormData({
      title: "",
      image_url: "",
      product_link: "",
      download_link: "",
    });
    setImageFile(null);
    setIsEditing(false);
    setCurrentProduct(null);
  };

  if (loading && !isEditing) return <LoadingSpinner size="large" />;
  if (error)
    return (
      <div className="error-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );

  return (
    <div className="page-content">
      <ToastContainer position="top-right" />
      <h2>Product Management</h2>

      <div className="partner-selection">
        <label htmlFor="partner-select">Select Partner</label>
        <select
          id="partner-select"
          value={selectedPartner?.id || ""}
          onChange={(e) => {
            const partner = partners.find((p) => p.id === e.target.value);
            setSelectedPartner(partner);
            setFormData({
              title: "",
              image_url: "",
              product_link: "",
              download_link: "",
            });
            setImageFile(null);
            setIsEditing(false);
            setCurrentProduct(null);
          }}
          className="partner-select"
        >
          <option value="" disabled>
            Choose a partner to manage products...
          </option>
          {partners.map((partner) => (
            <option key={partner.id} value={partner.id}>
              {partner.name}
            </option>
          ))}
        </select>
      </div>

      {selectedPartner && (
        <>
          <h3>{isEditing ? "Edit Product" : "Add New Product"}</h3>
          <form onSubmit={handleSubmit} className="product-form">
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="product_link">Product Link</label>
              <input
                type="url"
                id="product_link"
                name="product_link"
                value={formData.product_link}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="download_link">Download Link</label>
              <input
                type="url"
                id="download_link"
                name="download_link"
                value={formData.download_link}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="image">Product Image</label>
              <div className="file-input-container">
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="file-input"
                />
                <label htmlFor="image" className="file-input-label">
                  <ImageIcon size={20} />
                  <span>{imageFile ? imageFile.name : "Choose an image"}</span>
                </label>
              </div>
              {formData.image_url && !imageFile && (
                <div className="current-image">
                  <img src={formData.image_url} alt="Current" />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-button">
                {isEditing ? "Update Product" : "Add Product"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="cancel-button"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="products-list">
            <h3>Products for {selectedPartner.name}</h3>
            <div className="products-grid">
              {products.map((product) => (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    <img src={product.image_url} alt={product.title} />
                  </div>
                  <div className="product-content">
                    <h4>{product.title}</h4>
                    <div className="product-links">
                      <a
                        href={product.product_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-button"
                      >
                        <Link size={16} />
                        Product Link
                      </a>
                      <a
                        href={product.download_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-button"
                      >
                        <Download size={16} />
                        Download
                      </a>
                    </div>
                    <div className="product-actions">
                      <button
                        onClick={() => handleEdit(product)}
                        className="edit-button"
                      >
                        <Edit2 size={16} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="delete-button"
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductManagement;
